import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  getCloudinarySrcSet,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';
import { getSkuProductPrices } from 'data/lib/product';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponSale, discountedPrice } from 'ecommerce/lib/coupon';
import {
  formatDayRange,
  formatShortDate,
  holidayOrderBy,
  holidaySeasonYear,
  withProduction,
} from 'ecommerce/lib/shippingSchedule';
import { formatPrice } from 'utils/format';

import DesignTabs, { DESIGNS_ID } from '../../components/holidayCards/designTabs';
import {
  FinalCta,
  HERO_SIZES,
  Hero,
  HolidayBand,
  HowItWorks,
  Pricing,
  Testimonials,
} from '../../components/holidayCards/sections';
import LandingEditorial from '../../components/landing/editorial';
import { ctaTracker, productImage } from '../../components/landing/shared';
import { Head, Layout } from '../../components/layout';
import NewsletterTile from '../../components/newsletter/newsletterTile';
import ProductFaq from '../../components/product/productFaq';
import { CARDS_SHIPPING } from '../../lib/cardsShipping';
import {
  FOIL_TAB,
  cardsQuery,
  designCount,
  holidayCardTabs,
  isFoil,
  parseCardsQuery,
  priceTable,
  sharedTiers,
  startingTier,
} from '../../lib/holidayCards.mjs';
import SETTINGS from '../../settings';
import CARDS from '../../specifications/cards';

// Custom Photo Holiday Cards landing page (docs/holiday-cards-page.md). Title, meta, H1 and the
// editorial block come from content/categories/custom-photo-holiday-cards.md, designs and prices
// from the catalog, order-by dates from the shipping schedule, the rest is design copy.
const CATEGORY_ID = 'custom-photo-holiday-cards';
const UPLOAD_PORTRAIT_ID = 'photo-card-114';
const UPLOAD_LANDSCAPE_ID = 'photo-card-113';
const track = ctaTracker('holiday_cards_page');

const HERO_IMAGE = productImage('photo-holiday-cards/01');

// Panel copy per tab. `countNoun` completes "27 Christmas designs".
const TAB_COPY = {
  christmas: {
    countNoun: 'Christmas designs',
    title: 'Photo Christmas cards',
    body: "Hand-lettered Merry Christmas script, bold red blocks, holly borders and gold foil stars. Fill one with a single family portrait or a grid of the year's best moments, then sign it from everyone, the dog included.",
    christmas: {
      script: 'Merry, bright and full of your people.',
      image: productImage('photo-card-foil/3'),
    },
  },
  holiday: {
    countNoun: 'Holiday designs',
    icon: 'sparkles',
    title: 'Holiday photo cards',
    body: "Happy Holidays, Season's Greetings, Joy and Peace. The biggest collection, with cozy illustrations, garlands, cookies and gold foil trees, for one card that suits the whole address list.",
    image: productImage('photo-holiday-cards/02'),
  },
  'new-year': {
    countNoun: 'New Year designs',
    icon: 'partyPopper',
    title: 'New Year photo cards',
    body: 'Champagne toasts, fireworks and confetti. A New Year card gives you a few extra weeks and a fresh-start reason to share the year in photos.',
    image: productImage('photo-card-foil/8'),
  },
  hanukkah: {
    countNoun: 'Hanukkah designs',
    icon: 'sparkles',
    title: 'Hanukkah photo cards',
    body: 'Script lettering and scattered stars in gold foil and classic ink. Gather the family in one photo and wish everyone a happy Hanukkah.',
    image: productImage('photo-holiday-cards/05'),
  },
  diwali: {
    countNoun: 'Diwali designs',
    icon: 'sparkles',
    title: 'Diwali photo cards',
    body: 'A hand-drawn diya with an ochre flame on warm cream. Gather everyone in their festive best for one photo and send Diwali wishes to family near and far.',
    // No Diwali gallery photo yet, the intro shows the design's own SKU photo (no width ladder).
    image: { imageKey: 'photo-card-167', prefix: 'sku/', type: 'big' },
  },
  [FOIL_TAB]: {
    countNoun: 'gold foil designs',
    icon: 'sparkles',
    title: 'Foil holiday cards',
    body: 'Gold foil lettering, stars and trims catch the light when the card comes out of the envelope. The foil is coated in a matte, soft-touch finish, so it feels as good as it looks. Every occasion tab also has a Gold foil only switch.',
    image: productImage('photo-card-foil/7'),
  },
};

const STEPS = [
  {
    title: 'Pick a design',
    body: 'Browse by occasion and finish, from hand-lettered Christmas script to gold foil New Year. Every card is a flat 5×7.',
    image: productImage('photo-card-nonfoil/3'),
  },
  {
    title: 'Add your photos and words',
    body: 'Upload from your phone or computer, add your names and a message. The back has room for more photos or a family update at no extra charge.',
    image: productImage('photo-card-foil/2'),
  },
  {
    title: 'We print and pack the envelopes',
    body: 'Your cards are printed on thick matte paper and arrive with White or Oatmeal envelopes. Add return and recipient addresses on White envelopes for $0.30 a card.',
    image: productImage('photo-card-foil/4'),
  },
];

// Product facts from content/products/photo-card.md.
const SPECS = [
  ['Size', 'Flat 5×7 card'],
  ['Paper', 'Thick matte, certified to FSC standards'],
  ['Back side', 'Photos or a message, no extra charge'],
  ['Envelopes', 'White or Oatmeal, one per card'],
  ['Addressing', 'Return and recipient, $0.30 a card, White envelopes only'],
  ['Designs', 'One design per set'],
];

// Testimonials of the photo-card and photo-card-foil product content.
const QUOTES = [
  {
    quote:
      "We love how our Christmas cards turned out this year, and we're so happy we had them printed through Parabo Press.",
    author: '@annamariahouse',
  },
  {
    quote:
      'Our Christmas cards have arrived! So in love with how they turned out! Parabo Press has so many great card options to choose from!',
    author: '@lifewiththegolinos',
  },
];

// FAQPage JSON-LD source and the rendered accordion. The order-by answer is added in season.
const FAQ = [
  {
    question: 'How many cards come in a set?',
    answer:
      'A set starts at 20 cards. Add more in groups of 10, and the price per card drops as the set grows.',
  },
  {
    question: 'Do envelopes come with my cards?',
    answer:
      'Yes. Every card comes with an envelope in White or Oatmeal. Envelopes carry Parabo Press branding.',
  },
  {
    question: 'Can you address my envelopes?',
    answer:
      "Yes, on White envelopes. We print your return address and your recipients' addresses for $0.30 a card. Upload your list of recipients and we add them for you.",
  },
  {
    question: 'Can I pick different designs in one set?',
    answer: 'No. All cards in a set use one design.',
  },
  {
    question: 'Can I upload my own card design?',
    answer:
      'Yes. Choose Upload Your Own and upload a JPG or PNG. Custom foil cards are not available.',
  },
  {
    question: 'Can I change the color of the foil?',
    answer: 'No. Foil designs come in gold only.',
  },
  {
    question: 'Who designs the cards?',
    answer: "Independent designers and illustrators. The designer is credited on each card's page.",
  },
];

const monthDay = (date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

// Order-by dates for the cards category, only while the season's schedule is published.
const holidayDates = (today, skuProducts) => {
  const year = holidaySeasonYear(new Date(today));
  if (!year) return null;
  const [cards] = withProduction([CARDS_SHIPPING], skuProducts);
  const production = cards
    ? ` During the holiday peak, cards take ${formatDayRange(cards.peak)} business days to produce.`
    : '';
  const standard = holidayOrderBy(CARDS_SHIPPING, 'standard', year);
  const express = holidayOrderBy(CARDS_SHIPPING, 'express', year);
  return {
    year,
    standard: formatShortDate(standard),
    express: formatShortDate(express),
    short: `Order by ${monthDay(standard)} for Christmas`,
    answer: `For delivery by Christmas in the contiguous US, order by ${formatShortDate(standard)} with Standard shipping or ${formatShortDate(express)} with Express.${production} Delivery is not guaranteed.`,
  };
};

// A card SKU as its own product, so the claimed coupon's restriction is checked against it.
const cardProduct = (sku) => ({
  id: sku.id,
  currency: sku.currency,
  options: [sku],
  bundles: [],
  prices: getSkuProductPrices(sku),
});

// ?theme=Upload Your Own (linked from the card FAQs) lands on the upload block.
const scrollToUpload = () => document.getElementById('upload-your-own')?.scrollIntoView();

const scrollToDesigns = () => {
  const section = document.getElementById(DESIGNS_ID);
  if (section && section.getBoundingClientRect().top < 0) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const HolidayCardsPage = ({ content, query, today }) => {
  const router = useRouter();
  const dataContext = useDataContext();
  const { previewSummary } = useClaimedCoupon();
  const category = dataContext.getCategory(CATEGORY_ID);
  const title = category.heroTitle ?? category.name;
  const hub = dataContext.getCategory('cards');
  const breadcrumb = [
    { name: 'Home', url: '/' },
    { name: hub.name, url: hub.url },
    { name: title },
  ];

  // gather
  const { total, tabs } = useMemo(() => {
    const built = holidayCardTabs(dataContext.skuProducts, CARDS);
    return {
      total: built.total,
      tabs: built.tabs.map((tab) => ({
        ...tab,
        ...TAB_COPY[tab.id],
        eyebrow: designCount(tab.designs.length, TAB_COPY[tab.id].countNoun),
        designs: tab.designs.map((sku) => ({
          sku,
          foil: isFoil(sku.id),
          meta: [CARDS[sku.id].theme, isFoil(sku.id) && 'Gold foil'].filter(Boolean).join(' · '),
        })),
      })),
    };
  }, [dataContext.skuProducts]);
  const [initial] = useState(() => parseCardsQuery(query, tabs));
  const [selected, setSelected] = useState(initial.tab);
  const [foilOnly, setFoilOnly] = useState(initial.foilOnly);

  // Keep the tab in sync with the URL: links with other params (footer, blog) keep the page
  // mounted, and Back / Forward onto entries of our own tab updates arrive as shallow route
  // changes. Our own updates parse to the state already set.
  useEffect(() => {
    const handleRouteChange = (url) => {
      const params = new URL(url, window.location.origin).searchParams;
      const next = parseCardsQuery(Object.fromEntries(params), tabs);
      setSelected(next.tab);
      setFoilOnly(next.foilOnly);
      // After the tab state renders and Next's own scroll to the top.
      if (next.upload) requestAnimationFrame(scrollToUpload);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router.events, tabs]);

  // normalize: classic and foil reference SKUs for the hero price and the pricing table
  const themed = tabs.flatMap((tab) => tab.designs.map((design) => design.sku));
  const classic = themed.find((sku) => !isFoil(sku.id));
  const foil = themed.find((sku) => isFoil(sku.id));
  const classicTier = classic && startingTier(classic);
  const foilTier = foil && startingTier(foil);
  const dates = holidayDates(today, dataContext.skuProducts);
  const faq = dates
    ? [{ question: 'When should I order for Christmas delivery?', answer: dates.answer }, ...FAQ]
    : FAQ;

  // Per-card coupon price in the hero, PriceTag pattern. Tiles show set prices instead.
  const heroDiscount =
    classic && couponSale(previewSummary, cardProduct(classic))?.discountedPriceFrom != null
      ? discountedPrice(previewSummary, classicTier.price, classic.id)
      : null;
  const heroPrice = classicTier && {
    price: formatPrice(heroDiscount ?? classicTier.price, classic.currency),
    originalPrice:
      heroDiscount != null ? formatPrice(classicTier.price, classic.currency) : undefined,
  };

  // The table only shows prices every live design of a finish shares, upload cards included.
  // Tiles price each design on their own.
  const familySkus = dataContext.skuProducts.filter((sku) => CARDS[sku.id]?.family === 'holiday');
  const foilSkus = familySkus.filter((sku) => isFoil(sku.id));
  const classicTiers = sharedTiers(familySkus.filter((sku) => !isFoil(sku.id)));
  const foilTiers = sharedTiers(foilSkus);
  const priceRows =
    classicTiers && (foilTiers || foilSkus.length === 0)
      ? priceTable({ classic: classicTiers, foil: foilTiers }).map((row) => ({
          label: row.label,
          classic: formatPrice(row.classic, familySkus[0].currency),
          foil: row.foil != null ? formatPrice(row.foil, familySkus[0].currency) : undefined,
        }))
      : [];

  const panelTabs = tabs.map((tab) =>
    tab.id === FOIL_TAB && foilTier
      ? {
          ...tab,
          facts: [
            'Gold foil only, across Christmas, New Year, Hanukkah and Holiday designs',
            `From ${formatPrice(foilTier.price, foil.currency)} a card in a set of ${foilTier.quantity}`,
            'Same 5×7 matte card, envelopes included',
          ],
        }
      : tab,
  );

  // act
  const updateUrl = (state) => {
    router.replace(
      { pathname: router.pathname, query: cardsQuery(router.query, state) },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
  };

  const handleSelect = (tab, location) => {
    track('tab', location, tab)();
    setSelected(tab);
    setFoilOnly(false);
    updateUrl({ tab, foilOnly: false });
    if (location === 'final_cta') {
      document.getElementById(DESIGNS_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      scrollToDesigns();
    }
  };

  const handleFoilChange = (value) => {
    track('foil_only', 'tabs', value ? 'on' : 'off')();
    setFoilOnly(value);
    updateUrl({ tab: selected, foilOnly: value });
  };

  const tileProps = (design) => {
    const tier = startingTier(design.sku);
    return { tier, sale: couponSale(previewSummary, cardProduct(design.sku)) };
  };

  useEffect(() => {
    if (initial.upload) scrollToUpload();
  }, [initial.upload]);

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({ imageKey: HERO_IMAGE.imageKey, prefix: HERO_IMAGE.prefix })}
        heroImageSrcSet={getCloudinarySrcSet(HERO_IMAGE)}
        heroImageSizes={HERO_SIZES}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />
      <ScriptMarkupFaq items={faq} />

      <Layout>
        <div className="lnd hc">
          {dates && (
            <HolidayBand
              year={dates.year}
              standard={dates.standard}
              express={dates.express}
              track={track}
            />
          )}

          <Hero
            title={title}
            breadcrumb={breadcrumb}
            total={total}
            price={heroPrice}
            setSize={classicTier?.quantity}
            image={HERO_IMAGE}
            onChoose={track('choose_design', 'hero')}
          />

          <DesignTabs
            tabs={panelTabs}
            selected={selected}
            foilOnly={foilOnly}
            onSelect={handleSelect}
            onFoilChange={handleFoilChange}
            deadline={dates?.short}
            tileProps={tileProps}
            track={track}
          />

          <HowItWorks
            steps={STEPS}
            uploadIds={{
              portrait: dataContext.getSkuProduct(UPLOAD_PORTRAIT_ID) ? UPLOAD_PORTRAIT_ID : null,
              landscape: dataContext.getSkuProduct(UPLOAD_LANDSCAPE_ID)
                ? UPLOAD_LANDSCAPE_ID
                : null,
            }}
            track={track}
          />

          {priceRows.length > 0 && (
            <Pricing
              rows={priceRows}
              setSize={classicTier.quantity}
              specs={SPECS}
              image={productImage('photo-card-foil/6')}
            />
          )}

          <Testimonials image={productImage('photo-holiday-cards/04')} quotes={QUOTES} />


          <section id="faq" className="hc-section hc-faq">
            <div className="container">
              <ProductFaq
                eyebrow="Good to know"
                title="Holiday card questions"
                intro={false}
                items={faq.map(({ question, answer }) => ({
                  caption: question,
                  description: answer,
                }))}
              />
            </div>
          </section>

          <div id="about" className="hc-about">
            <LandingEditorial
              eyebrow="About holiday cards"
              title="Holiday cards from your family photos"
              html={content.extraDescription}
            />
          </div>

          <FinalCta
            tabs={panelTabs}
            image={productImage('photo-card-nonfoil/8')}
            deadline={dates?.short}
            onSelect={handleSelect}
            track={track}
          />

          <NewsletterTile />
        </div>
      </Layout>
    </>
  );
};

HolidayCardsPage.getInitialProps = async ({ query }) => ({
  content: await contentApi.category(CATEGORY_ID),
  query,
  // Dated on the server so the order-by dates render the same on the client.
  today: new Date().toISOString(),
});

HolidayCardsPage.propTypes = {
  content: PropTypes.object.isRequired,
  query: PropTypes.object.isRequired,
  today: PropTypes.string.isRequired,
};

export default HolidayCardsPage;
